// Global "Ask Reggie" drawer for the Analytics section.
//
// Mounted once by NavigationWrapper but rendered through a portal onto
// document.body: the navigation wrapper is CSS-transformed, which would turn
// position: fixed into position: absolute and pin the drawer to the left.
// It slides in from the right over any /analytics page and stays mounted while
// the user is in the section, so closing it keeps the conversation (and lets an
// in-flight answer finish). It closes itself when they leave the section, and
// renders nothing unless the organization has isRhinoAnalystEnabled (CCR
// toggle) and the service URL is configured.
//
// Conversations are loaded from the rhinoanalyst service per user+organization
// (summaries up front, messages when a chat is opened); nothing is kept in the browser.

import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import cx from 'classnames';

import RhinoAnalystPanel from './RhinoAnalystPanel';
import {
  closeAskReggie, conversationStorageKey, getAskReggieState, hydrateConversations, setUsageMonth,
} from '../reducers/rhinoanalystReducer';
import { getCurrentOrg } from '../selectors/organizationSelectors';
import { getLoggedInUser } from '../selectors/userSelectors';
import { isRhinoAnalystConfigured, listConversations, getUsage } from '../services/RhinoAnalystService';

export const isAskReggieAvailable = (organization) => !!organization?.isRhinoAnalystEnabled && isRhinoAnalystConfigured();

const RhinoAnalystDrawer = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { isOpen, context, storageKey } = useSelector(getAskReggieState);
  const currentOrganization = useSelector(getCurrentOrg);
  const currentUser = useSelector(getLoggedInUser);

  const available = isAskReggieAvailable(currentOrganization);
  const inAnalytics = location.pathname.startsWith('/analytics');
  const nextKey = conversationStorageKey(currentUser?.id, currentOrganization?.id);

  // Load this user+org's conversations from the service whenever the identity
  // changes. A failed load leaves an empty list; chatting still works.
  useEffect(() => {
    if (!nextKey || nextKey === storageKey || !available) return undefined;
    const controller = new AbortController();
    listConversations({ signal: controller.signal })
      .then((data) => dispatch(hydrateConversations({ storageKey: nextKey, conversations: data?.conversations || [] })))
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error(err); // eslint-disable-line no-console
        dispatch(hydrateConversations({ storageKey: nextKey, conversations: [] }));
      });
    return () => controller.abort();
  }, [nextKey, storageKey, available, dispatch]);

  // Month-to-date usage for the footer, refreshed each time the drawer opens.
  useEffect(() => {
    if (!isOpen || !available) return undefined;
    const controller = new AbortController();
    getUsage({ signal: controller.signal })
      .then((data) => {
        if (data?.org) dispatch(setUsageMonth({ month: data.month, turns: data.org.turns, limit: data.limit }));
      })
      .catch(() => { /* usage line is optional */ });
    return () => controller.abort();
  }, [isOpen, available, dispatch]);

  useEffect(() => {
    if (isOpen && (!inAnalytics || !available)) dispatch(closeAskReggie());
  }, [isOpen, inAnalytics, available, dispatch]);

  if (!available || !inAnalytics || typeof document === 'undefined') return null;

  return ReactDOM.createPortal(
    <div className={cx('rhinoanalyst-drawer', { 'is-open': isOpen })} aria-hidden={!isOpen}>
      <RhinoAnalystPanel
        onClose={() => dispatch(closeAskReggie())}
        currentUser={currentUser}
        contextLabel={context.label}
        dateRange={context.dateRange}
        context={{ page: context.page, filters: context.filters }}
      />
    </div>,
    document.body,
  );
};

export default RhinoAnalystDrawer;
